import React, { forwardRef, useCallback, useId, useRef } from 'react';
import { Cancel01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import * as styles from './Input.module.css';

export type InputBackground = 'white' | 'gray';
export type InputVariant = 'primary' | 'secondary' | 'outline';
export type InputSize = 'sm' | 'md' | 'lg';

export interface InputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'size'
  > {
  variant?: InputVariant;
  size?: InputSize | React.InputHTMLAttributes<HTMLInputElement>['size'];
  label?: React.ReactNode;
  error?: React.ReactNode;
  background?: InputBackground;
  fullWidth?: boolean;
  wrapperClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  clearable?: boolean;
  onValueChange?: (value: string) => void;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      variant,
      size = 'md',
      label,
      error,
      background,
      fullWidth = false,
      className = '',
      wrapperClassName = '',
      labelClassName = '',
      inputClassName = '',
      leftIcon,
      rightIcon,
      clearable = false,
      onValueChange,
      onChange,
      disabled,
      readOnly,
      id,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
      ...props
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const setInputRef = useCallback(
      (input: HTMLInputElement | null) => {
        inputRef.current = input;
        if (typeof ref === 'function') {
          return ref(input);
        }
        if (ref) ref.current = input;
      },
      [ref]
    );
    const generatedId = useId();
    const generatedErrorId = useId();
    const inputId = id ?? generatedId;
    const hasError = Boolean(error);
    const hasLeftIcon = Boolean(leftIcon);
    const hasRightIcon = Boolean(rightIcon);
    const visualSize = typeof size === 'string' ? size : 'md';
    const nativeSize = typeof size === 'number' ? size : undefined;
    const resolvedVariant =
      variant ?? (background === 'gray' ? 'secondary' : 'primary');
    const s = styles as Record<string, string>;

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = event => {
      onValueChange?.(event.target.value);
      onChange?.(event);
    };

    const handleClear = () => {
      const input = inputRef.current;
      if (!input || disabled || readOnly) return;

      // Use the native setter so React observes the change in controlled inputs.
      const setValue = Object.getOwnPropertyDescriptor(
        HTMLInputElement.prototype,
        'value'
      )?.set;
      setValue?.call(input, '');
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.focus();
    };

    const wrapperClasses = [
      s.wrapper || '',
      fullWidth ? s.fullWidth || '' : '',
      disabled ? s.disabled || '' : '',
      wrapperClassName,
    ]
      .filter(Boolean)
      .join(' ');

    const inputClasses = [
      s.input || '',
      s[resolvedVariant] || '',
      s[visualSize] || '',
      hasError ? s.hasError || '' : '',
      hasLeftIcon ? s.input_with_left_icon || '' : '',
      hasRightIcon || clearable ? s.input_with_right_icon || '' : '',
      hasRightIcon && clearable ? s.input_with_right_actions || '' : '',
      className,
      inputClassName,
    ]
      .filter(Boolean)
      .join(' ');

    const describedBy = [ariaDescribedBy, hasError ? generatedErrorId : '']
      .filter(Boolean)
      .join(' ');

    const controlClasses = [s.control || '', fullWidth ? s.fullWidth || '' : '']
      .filter(Boolean)
      .join(' ');

    return (
      <div className={wrapperClasses}>
        {label && (
          <label
            className={`${s.label || ''} ${labelClassName}`.trim()}
            htmlFor={inputId}
          >
            {label}
          </label>
        )}
        <div className={controlClasses}>
          {hasLeftIcon && <span className={s.icon_left || ''}>{leftIcon}</span>}
          <input
            ref={setInputRef}
            id={inputId}
            size={nativeSize}
            disabled={disabled}
            readOnly={readOnly}
            aria-describedby={describedBy || undefined}
            aria-invalid={ariaInvalid ?? (hasError ? true : undefined)}
            className={inputClasses}
            onChange={handleChange}
            {...props}
          />
          {(hasRightIcon || clearable) && (
            <span className={s.icon_right || ''}>
              {rightIcon}
              {clearable && (
                <button
                  type="button"
                  className={s.clear_button || ''}
                  aria-label="Очистить поле"
                  disabled={disabled || readOnly}
                  onClick={handleClear}
                >
                  <HugeiconsIcon icon={Cancel01Icon} size={16} aria-hidden />
                </button>
              )}
            </span>
          )}
        </div>
        {hasError && (
          <span className={s.error || ''} id={generatedErrorId}>
            {error}
          </span>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
export default Input;
